/**
 * THE HAIR REGION AND ITS TREATMENT — ADR-039 T1, promoted from the probe
 * (docs/team-briefs/HAIR_CONNECTEDNESS_PROBE_2026-09-30.md, whose tables
 * calibrated every constant here).
 *
 *  1. SKULL: `placeSkull` (eye bones, then the neck method), or a person's
 *     declared skull read back into the head frame — never re-measured.
 *  2. REGION: flesh over the skull — vertices the Head subtree dominates, or
 *     co-weights at ≥ 0.1, lying more than 0.15 R outside the skull, off the
 *     face mask (below the brow, ahead of the skull centre, within its breadth)
 *     and out of the neck tube. It is a CANDIDATE: on short hair it takes ears
 *     and nape too (the probe's Daria), which is why a person confirms it.
 *  3. CONNECTEDNESS: a hair vertex is FREE when it lies beyond the band
 *     (4% of body height) from both the skull and the body from the neck down.
 *     Free vertices group into welded clumps; a clump holding ≥ 2% of the
 *     region whose REACH out of the skull is ≥ 2 R is a CHAIN clump (attached
 *     masses reach ≤ 1.3 R, free ones ≥ 5.1 R in the probe — 2 R sits in the gap).
 *  4. TREATMENT: no chain clump → `attached` (J4 jiggle, T2); chain clumps
 *     holding ≥ 50% of the region → `free` (spring chains, T2); else `mixed`.
 *
 * Reach first, share second: Shirley's fused curls read 67% free at a 2% band,
 * yet reach 1.2 R — attached. Counts are of WELDED vertices (UV-seam twins
 * merge), as in the probe.
 *
 * Engine-free: arrays in, arrays out. Distances to the body use a uniform grid
 * whose cell is the band, so the "within the band of the body" test is exact
 * and costs a 27-cell look-up, not the probe's all-pairs scan.
 *
 * Depends on: hair/skull. Depended on by: the app's Hair tool and its door test.
 */
import {
  adoptSkull,
  headBody,
  pct,
  placeSkull,
  radial,
  skullFromDeclaration,
  skullToDeclaration,
  type Skull,
  type SkullDeclarationShape,
} from './skull.js';

/** Outside the skull by more than this × R before a vertex can be hair (the skin layer the inscribed proxy leaves). */
export const HAIR_SKULL_MARGIN = 0.15;
/** A vertex co-weighted to the Head subtree at least this much joins the region (Shirley's 1,063 clavicle-painted hair). */
export const HAIR_CO_WEIGHT = 0.1;
/** The contact band, as a fraction of body height: beyond it from skull and body, a vertex is free. */
export const HAIR_BAND = 0.04;
/** A free clump smaller than this share of the region is fringe, not a chain candidate. */
export const HAIR_CLUMP_MIN_SHARE = 0.02;
/** A free clump reaching this many skull radii out of the skull is a chain (a column, a wing, a ponytail). */
export const HAIR_CHAIN_REACH = 2;
/** Chain clumps holding at least this share of the region make the hair FREE. */
export const HAIR_FREE_CHAIN_SHARE = 0.5;

export type HairTreatment = 'attached' | 'free' | 'mixed';
export const HAIR_TREATMENTS = ['attached', 'free', 'mixed'] as const satisfies readonly HairTreatment[];

export interface HairBone {
  readonly name: string;
  readonly parent: string | null;
  readonly worldPosition: { readonly x: number; readonly y: number; readonly z: number };
}

export interface HairInput {
  /** Bind-world positions, xyz interleaved. */
  readonly positions: Float32Array;
  readonly indices: Uint32Array | Uint16Array;
  /** Four influences per vertex, as the FILE painted them (the generator's weights are what T1 measures). */
  readonly skin: { readonly index: ArrayLike<number>; readonly weight: ArrayLike<number> } | null;
  /** Skeleton bones in skin order, bind-world. */
  readonly bones: readonly HairBone[];
  /** Bones the skin does not list (HeadTop_End, head_end) — for the crown cap only. */
  readonly extraBones?: readonly HairBone[];
  /** The body's measured frame (the tail detector's: forward from the feet, height). */
  readonly frame: { readonly height: number; readonly forward: { readonly x: number; readonly z: number } };
  /** A person's skull, bind-world. Absent = measured. */
  readonly skull?: SkullDeclarationShape;
}

export interface HairClump {
  /** Welded vertices in the clump. */
  readonly vertices: number;
  /** Its share of the region (welded). */
  readonly share: number;
  /** The furthest it gets out of the skull, in skull radii. */
  readonly reachR: number;
  /** Large enough and reaching far enough to be a chain. */
  readonly chain: boolean;
}

export interface HairContamination {
  /** A bone outside the Head subtree that the generator painted onto the region. */
  readonly bone: string;
  /** Region vertices (welded) it carries any weight on. */
  readonly vertices: number;
  /** Region vertices it dominates. */
  readonly dominates: number;
  readonly maxWeight: number;
}

export interface HairReading {
  readonly skull: Skull;
  /** `declared` = a person's skull read back; `measured` = placeSkull's. */
  readonly skullSource: 'measured' | 'declared';
  /** The skull in bind-world — what a Confirm records. */
  readonly declaration: SkullDeclarationShape;
  readonly headBone: string;
  /** The Head bone and every bone under it. */
  readonly headBones: readonly string[];
  /** Hair vertices, every UV-seam twin included (raw indices). */
  readonly vertexIndices: Uint32Array;
  /** Faces whose three corners are hair — the zone's face set. */
  readonly faceIndices: Uint32Array;
  /** Welded hair vertices, and how many joined only by co-weight. */
  readonly welded: number;
  readonly coWeighted: number;
  /** Null = no flesh over the skull: nothing to offer. */
  readonly treatment: HairTreatment | null;
  /** Free clumps of at least HAIR_CLUMP_MIN_SHARE, largest first. */
  readonly clumps: readonly HairClump[];
  readonly chainShare: number;
  /** Free welded vertices over the region (evidence; the verdict reads reach and chain share). */
  readonly freeShare: number;
  /** The largest reach among the counted clumps, skull radii (0 when there are none). */
  readonly maxReachR: number;
  readonly contamination: readonly HairContamination[];
}

export interface HairRefusal {
  readonly refused: string;
}

/** Position-welded twins: one representative per exact (1e-5) position, as the probe welds. */
function weldPositions(P: Float32Array): { weld: Int32Array; reps: number[] } {
  const n = P.length / 3;
  const weld = new Int32Array(n);
  const key = new Map<string, number>();
  const reps: number[] = [];
  for (let i = 0; i < n; i++) {
    const k = `${Math.round(P[i * 3]! * 1e5)},${Math.round(P[i * 3 + 1]! * 1e5)},${Math.round(P[i * 3 + 2]! * 1e5)}`;
    const at = key.get(k);
    if (at === undefined) {
      key.set(k, i);
      weld[i] = i;
      reps.push(i);
    } else weld[i] = at;
  }
  return { weld, reps };
}

/** For each `from` vertex: is any `to` vertex within `d`? A uniform grid of cell `d`, 27 cells per query. */
function withinOf(P: Float32Array, from: readonly number[], to: readonly number[], d: number): Uint8Array {
  const out = new Uint8Array(P.length / 3);
  if (to.length === 0 || !(d > 0)) return out;
  const cell = (x: number): number => Math.floor(x / d);
  const hash = (ix: number, iy: number, iz: number): number => ((ix * 73856093) ^ (iy * 19349663) ^ (iz * 83492791)) | 0;
  const grid = new Map<number, number[]>();
  for (const j of to) {
    const h = hash(cell(P[j * 3]!), cell(P[j * 3 + 1]!), cell(P[j * 3 + 2]!));
    const bucket = grid.get(h);
    if (bucket === undefined) grid.set(h, [j]);
    else bucket.push(j);
  }
  const d2 = d * d;
  for (const i of from) {
    const x = P[i * 3]!;
    const y = P[i * 3 + 1]!;
    const z = P[i * 3 + 2]!;
    const cx = cell(x);
    const cy = cell(y);
    const cz = cell(z);
    search: for (let a = -1; a <= 1; a++)
      for (let b = -1; b <= 1; b++)
        for (let c = -1; c <= 1; c++) {
          const bucket = grid.get(hash(cx + a, cy + b, cz + c));
          if (bucket === undefined) continue;
          for (const j of bucket) {
            // a hash collision only adds candidates; the distance is exact
            if ((P[j * 3]! - x) ** 2 + (P[j * 3 + 1]! - y) ** 2 + (P[j * 3 + 2]! - z) ** 2 <= d2) {
              out[i] = 1;
              break search;
            }
          }
        }
  }
  return out;
}

/**
 * Measure the hair on a body: its skull, its region, its clumps and its
 * treatment. A refusal names why (no Head bone, no skin, no skull placed, a
 * declared skull this body cannot read back) — the caller surfaces it, and
 * nothing downstream runs.
 */
export function measureHair(input: HairInput): HairReading | HairRefusal {
  const P = input.positions;
  const n = P.length / 3;
  const { weld, reps } = weldPositions(P);
  const hb = headBody({ positions: P, skin: input.skin, bones: input.bones }, input.frame, reps, input.extraBones ?? []);
  if (typeof hb === 'string') return { refused: hb };
  const placed = placeSkull(hb);
  let skull: Skull;
  let skullSource: HairReading['skullSource'];
  if (input.skull !== undefined) {
    const e = skullFromDeclaration(hb, input.skull);
    if (typeof e === 'string') return { refused: e };
    skull = adoptSkull(typeof placed === 'string' ? null : placed, e, `declared by a person${typeof placed === 'string' ? '' : ` (measured: ${placed.method})`}`);
    skullSource = 'declared';
  } else {
    if (typeof placed === 'string') return { refused: placed };
    skull = placed;
    skullSource = 'measured';
  }
  const { L, headOwned, neckOwned, headW, bones } = hb;
  const S = skull.e;
  const { R, vE, vChin, F } = skull;
  const U = (i: number): number => L[i * 3]!;
  const V = (i: number): number => L[i * 3 + 1]!;
  const W = (i: number): number => L[i * 3 + 2]!;

  // ── the neck tube: Neck-owned flesh's median distance from the neck chain line, × 1.25 (the probe's) ──
  const H0 = bones[hb.headBone]!.worldPosition;
  const lowNeck = [...bones.keys()]
    .filter((i) => /neck/i.test(bones[i]!.name))
    .map((i) => bones[i]!.worldPosition)
    .reduce((m, q) => (q.y < m.y ? q : m), H0);
  const [ax, ay, az] = hb.toLocal(lowNeck);
  const segDist = (i: number): number => {
    const t = Math.max(0, Math.min(1, (-(U(i) - ax) * ax - (V(i) - ay) * ay - (W(i) - az) * az) / (ax * ax + ay * ay + az * az || 1)));
    return Math.hypot(U(i) - ax * (1 - t), V(i) - ay * (1 - t), W(i) - az * (1 - t));
  };
  const neckFlesh = reps.filter(neckOwned);
  // a rig whose neck bones own no flesh (the 78-joint presets paint the neck to Head / Spine02): no tube
  const neckR = neckFlesh.length >= 20 ? pct(neckFlesh.map(segDist), 0.5) : 0;

  // ── the region ──
  const dSkull = new Float32Array(n);
  for (const i of reps) dSkull[i] = radial(S, U(i), V(i), W(i));
  const faceMask = (i: number): boolean => V(i) < vE + 0.35 * F && W(i) > S.c[2] && Math.abs(U(i) - S.c[0]) < S.r[0];
  const neckTube = (i: number): boolean => V(i) < vChin && segDist(i) < 1.25 * neckR;
  const isHair = new Uint8Array(n);
  let coWeighted = 0;
  for (const i of reps) {
    if (!(dSkull[i]! > HAIR_SKULL_MARGIN * R) || faceMask(i) || neckTube(i)) continue;
    if (headOwned(i)) isHair[i] = 1;
    else if (headW[i]! >= HAIR_CO_WEIGHT) {
      isHair[i] = 1;
      coWeighted++;
    }
  }
  for (let i = 0; i < n; i++) isHair[i] = isHair[weld[i]!]!;
  const hair = reps.filter((i) => isHair[i] === 1);
  const headBones = [...hb.headSub].map((i) => bones[i]!.name);
  const declaration = skullToDeclaration(hb, S);
  const base = { skull, skullSource, declaration, headBone: bones[hb.headBone]!.name, headBones, coWeighted };
  if (hair.length === 0) {
    return {
      ...base,
      vertexIndices: new Uint32Array(0),
      faceIndices: new Uint32Array(0),
      welded: 0,
      treatment: null,
      clumps: [],
      chainShare: 0,
      freeShare: 0,
      maxReachR: 0,
      contamination: [],
    };
  }

  // ── contamination: the generator's non-Head bones inside the region ──
  const skin = input.skin!;
  const contam = new Map<number, { vertices: number; dominates: number; maxWeight: number }>();
  for (const i of hair) {
    for (let k = 0; k < 4; k++) {
      const b = skin.index[i * 4 + k]!;
      const w = skin.weight[i * 4 + k]!;
      if (w <= 0 || hb.headSub.has(b)) continue;
      const c = contam.get(b) ?? { vertices: 0, dominates: 0, maxWeight: 0 };
      c.vertices++;
      c.maxWeight = Math.max(c.maxWeight, w);
      contam.set(b, c);
    }
    if (!headOwned(i)) {
      const c = contam.get(hb.dominant[i]!);
      if (c !== undefined) c.dominates++;
    }
  }
  const contamination = [...contam]
    .map(([b, c]) => ({ bone: bones[b]?.name ?? `#${b}`, ...c }))
    .sort((a, b) => b.vertices - a.vertices || a.bone.localeCompare(b.bone));

  // ── connectedness: beyond the band from the skull AND from the body from the neck down ──
  const d0 = HAIR_BAND * input.frame.height;
  const body = reps.filter((i) => isHair[i] === 0 && !headOwned(i));
  const nearBody = withinOf(P, hair, body, d0);
  const free = new Uint8Array(n);
  let freeCount = 0;
  for (const i of hair)
    if (dSkull[i]! > d0 && nearBody[i] === 0) {
      free[i] = 1;
      freeCount++;
    }
  // clumps: free welded vertices joined by a triangle edge (union-find over the welded ids)
  const parent = new Int32Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (x: number): number => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]!]!;
      x = parent[x]!;
    }
    return x;
  };
  const I = input.indices;
  for (let t = 0; t + 2 < I.length; t += 3) {
    const a = weld[I[t]!]!;
    const b = weld[I[t + 1]!]!;
    const c = weld[I[t + 2]!]!;
    for (const [p, q] of [
      [a, b],
      [b, c],
      [c, a],
    ] as const) {
      if (free[p] === 1 && free[q] === 1) {
        const rp = find(p);
        const rq = find(q);
        if (rp !== rq) parent[rp] = rq;
      }
    }
  }
  const groups = new Map<number, { vertices: number; reach: number }>();
  for (const i of hair) {
    if (free[i] === 0) continue;
    const r = find(i);
    const g = groups.get(r) ?? { vertices: 0, reach: 0 };
    g.vertices++;
    g.reach = Math.max(g.reach, dSkull[i]!);
    groups.set(r, g);
  }
  const clumps: HairClump[] = [...groups.values()]
    .filter((g) => g.vertices >= HAIR_CLUMP_MIN_SHARE * hair.length)
    .sort((a, b) => b.vertices - a.vertices)
    .map((g) => ({ vertices: g.vertices, share: g.vertices / hair.length, reachR: g.reach / R, chain: g.reach >= HAIR_CHAIN_REACH * R }));
  const chainShare = clumps.filter((c) => c.chain).reduce((s, c) => s + c.share, 0);
  const treatment: HairTreatment = !clumps.some((c) => c.chain) ? 'attached' : chainShare >= HAIR_FREE_CHAIN_SHARE ? 'free' : 'mixed';

  // ── the zone's faces: three hair corners ──
  const faces: number[] = [];
  for (let f = 0; f * 3 + 2 < I.length; f++) if (isHair[I[f * 3]!] && isHair[I[f * 3 + 1]!] && isHair[I[f * 3 + 2]!]) faces.push(f);
  const vertexIndices: number[] = [];
  for (let i = 0; i < n; i++) if (isHair[i] === 1) vertexIndices.push(i);

  return {
    ...base,
    vertexIndices: Uint32Array.from(vertexIndices),
    faceIndices: Uint32Array.from(faces),
    welded: hair.length,
    treatment,
    clumps,
    chainShare,
    freeShare: freeCount / hair.length,
    maxReachR: clumps.length > 0 ? Math.max(...clumps.map((c) => c.reachR)) : 0,
    contamination,
  };
}

/** One line for the status bar: what was measured, in the probe's words. */
export function describeHair(r: HairReading): string {
  const mm = (m: number): string => (m * 1000).toFixed(0);
  const s = r.skull;
  const skull = `skull ${mm(s.e.r[0])}·${mm(s.e.r[1])}·${mm(s.e.r[2])} mm (R ${mm(s.R)}) ${r.skullSource === 'declared' ? 'as a person placed it' : `from ${s.method.startsWith('eye') ? 'the eye bones' : 'the neck'}`}`;
  if (r.treatment === null) return `No flesh over the skull · ${skull}.`;
  const chains = r.clumps.filter((c) => c.chain);
  const verdict =
    r.treatment === 'attached'
      ? `attached — no clump reaches ${HAIR_CHAIN_REACH} R (max ${r.maxReachR.toFixed(1)} R)`
      : `${r.treatment} — ${chains.length} chain clump${chains.length === 1 ? '' : 's'} hold ${(100 * r.chainShare).toFixed(0)}% (reach ${chains.map((c) => c.reachR.toFixed(1)).join(', ')} R)`;
  const top = r.contamination.find((c) => c.dominates > 0) ?? r.contamination[0];
  const foreign =
    top === undefined
      ? 'all of it painted to the Head'
      : `the generator painted ${top.bone} on ${top.vertices.toLocaleString('en-US')} of them${top.dominates > 0 ? ` (dominant on ${top.dominates.toLocaleString('en-US')})` : ''}`;
  return `Hair ${verdict} · ${r.welded.toLocaleString('en-US')} vertices, ${foreign} · ${skull}.`;
}
